import {
  AndroidBiometryStrength,
  BiometricAuth,
  BiometryError,
  BiometryErrorType,
  BiometryType
} from '@aparajita/capacitor-biometric-auth';
import { Capacitor } from '@capacitor/core';

// TODO: Localize messages

export interface BiometricAuthResult {
  success: boolean;
  error?: string;
  biometryType?: BiometryType;
}

export interface BiometricAvailability {
  isAvailable: boolean;
  biometryType?: BiometryType;
}

/**
 * Check if biometric authentication is available on the device
 * Uses strong biometry only (fingerprint on Android, Touch ID/Face ID on iOS)
 */
export const checkBiometricAvailability = async (): Promise<BiometricAvailability> => {
  const UNAVAILABLE: BiometricAvailability = { isAvailable: false };

  try {
    if (!Capacitor.isNativePlatform()) return UNAVAILABLE;

    const { biometryType, strongBiometryIsAvailable } = await BiometricAuth.checkBiometry();

    return { isAvailable: strongBiometryIsAvailable, biometryType };
  } catch (error) {
    console.error('[BiometricAuth] Error checking availability:', error);
    return UNAVAILABLE;
  }
};

/**
 * Perform biometric authentication
 * Uses strong biometry only (fingerprint on Android, Touch ID/Face ID on iOS)
 */
export const authenticateWithBiometrics = async (reason: string): Promise<BiometricAuthResult> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return { success: false, error: 'Biometric authentication is not available on web platform' };
    }

    const availability = await checkBiometricAvailability();
    if (!availability.isAvailable) {
      return { success: false, error: 'Biometric authentication is not available on this device' };
    }

    await BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
      androidBiometryStrength: AndroidBiometryStrength.strong
    });

    return { success: true, biometryType: availability.biometryType };
  } catch (error) {
    if (error instanceof BiometryError) {
      return { success: false, error: getBiometricErrorMessage(error) };
    }

    return { success: false, error: 'An unknown error occurred during biometric authentication' };
  }
};

/**
 * Convert biometric auth errors to user-friendly messages
 */
// TODO: consider remove it since we don't show it to user.
const getBiometricErrorMessage = (error: BiometryError): string => {
  switch (error.code) {
    case BiometryErrorType.biometryNotAvailable:
      return 'Biometric authentication is not available on this device';
    case BiometryErrorType.biometryNotEnrolled:
      return 'No biometric credentials are enrolled on this device';
    case BiometryErrorType.biometryLockout:
      return 'Biometric authentication is temporarily locked. Please try again later.';
    case BiometryErrorType.userCancel:
      return 'Authentication was cancelled by user';
    case BiometryErrorType.userFallback:
      return 'User chose to use device passcode instead';
    case BiometryErrorType.systemCancel:
      return 'Authentication was cancelled by the system';
    case BiometryErrorType.passcodeNotSet:
      return 'Device passcode is not set';
    case BiometryErrorType.authenticationFailed:
      return 'Biometric authentication failed. Please try again.';
    default:
      return error.message || 'An unknown error occurred during biometric authentication';
  }
};

/**
 * Get human-readable biometry type name
 */
export const getBiometryTypeName = (biometryType?: BiometryType): string | null => {
  switch (biometryType) {
    case BiometryType.fingerprintAuthentication:
      return 'Fingerprint';
    case BiometryType.faceAuthentication:
      return 'Face ID';
    case BiometryType.irisAuthentication:
      return 'Iris';
    default:
      return null;
  }
};
